"use client";

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { API_URL } from "@/lib/config";
import { parseGpx } from "./gpxParser";
import { sliceTrack, simplifyTrack, routeToGeojsonCoords } from "./gpxGeometry";
import type {
  GpxRoute,
  GpxRouteSettings,
  SavedGpxRoute,
  NearbyRouteItem,
  Waypoint,
} from "./types";
import { DEFAULT_GPX_SETTINGS, GPX_STORAGE_KEY } from "./types";

const API = API_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

function readSaved(): SavedGpxRoute[] {
  try { return JSON.parse(localStorage.getItem(GPX_STORAGE_KEY) ?? "[]"); }
  catch { return []; }
}

function writeSaved(routes: SavedGpxRoute[]) {
  try { localStorage.setItem(GPX_STORAGE_KEY, JSON.stringify(routes)); }
  catch {}
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function xmlEscape(s: string): string {
  return s.replace(/[<>&"]/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c),
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useGpxRoute() {
  const [isOpen, setIsOpen] = useState(false);
  const [gpxRoute, setGpxRoute] = useState<GpxRoute | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [settings, setSettingsState] = useState<GpxRouteSettings>(DEFAULT_GPX_SETTINGS);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [proximityResults, setProximityResults] = useState<NearbyRouteItem[]>([]);
  const [proximityLoading, setProximityLoading] = useState(false);
  const [proximityCount, setProximityCount] = useState<number | null>(null);
  const [savedRoutes, setSavedRoutes] = useState<SavedGpxRoute[]>([]);
  const [savedFlash, setSavedFlash] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [tourCreating, setTourCreating] = useState(false);
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const proximityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load saved routes on mount + restore from URL param ──────────────────
  useEffect(() => {
    setSavedRoutes(readSaved());
    try {
      const param = new URLSearchParams(window.location.search).get("gpx");
      if (!param) return;
      const decoded = JSON.parse(atob(param));
      if (decoded?.rawTrkpts?.length) {
        setGpxRoute({
          name: decoded.name ?? "Shared Route",
          trkpts: decoded.rawTrkpts,
          totalDistanceM: decoded.rawTrkpts[decoded.rawTrkpts.length - 1]?.distFromStart ?? 0,
          elevationGainM: 0,
          elevationLossM: 0,
          durationSec: null,
        });
        if (decoded.settings) setSettingsState({ ...DEFAULT_GPX_SETTINGS, ...decoded.settings });
        if (decoded.waypoints) setWaypoints(decoded.waypoints);
        setIsOpen(true);
        const url = new URL(window.location.href);
        url.searchParams.delete("gpx");
        window.history.replaceState(null, "", url.toString());
      }
    } catch {}
  }, []);

  // ── Derived display track (after trim + simplify) ─────────────────────────
  const displayTrkpts = useMemo(() => {
    if (!gpxRoute) return [];
    let pts = sliceTrack(gpxRoute.trkpts, settings.trimStart, settings.trimEnd);
    if (settings.simplifyTolerance > 0) pts = simplifyTrack(pts, settings.simplifyTolerance);
    return pts;
  }, [gpxRoute, settings.trimStart, settings.trimEnd, settings.simplifyTolerance]);

  const trimmedGeoJson = useMemo(
    () => routeToGeojsonCoords(displayTrkpts),
    [displayTrkpts],
  );

  // ── Settings updater ──────────────────────────────────────────────────────
  const updateSetting = useCallback(<K extends keyof GpxRouteSettings>(
    key: K,
    value: GpxRouteSettings[K],
  ) => {
    setSettingsState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── File upload + parse ───────────────────────────────────────────────────
  const handleFileUpload = useCallback((file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setParseError("File is too large (max 10 MB). Please trim the GPX first.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const route = parseGpx(text);
        setGpxRoute(route);
        setParseError(null);
        setWaypoints([]);
        setProximityResults([]);
        setSettingsState((prev) => ({
          ...prev,
          routeName: route.name,
          trimStart: 0,
          trimEnd: 1,
        }));
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse GPX file.");
      }
    };
    reader.onerror = () => setParseError("Could not read the file.");
    reader.readAsText(file);
  }, []);

  // ── Proximity search ─────────────────────────────────────────────────────
  const searchProximity = useCallback(async () => {
    if (displayTrkpts.length < 2) return;
    setProximityLoading(true);
    setProximityCount(null);
    try {
      // Cap at 2000 pts for the API call — PostGIS doesn't need full resolution
      // for proximity search; thin evenly if over limit.
      const MAX_COORDS = 2000;
      let coords = displayTrkpts;
      if (coords.length > MAX_COORDS) {
        const step = coords.length / MAX_COORDS;
        coords = Array.from({ length: MAX_COORDS }, (_, i) => coords[Math.round(i * step)]);
      }
      const res = await fetch(`${API}/api/explore/nearby-route`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coordinates: coords.map((p) => [p.lng, p.lat]),
          radius_m: settings.proximityRadiusM,
          my_photos_only: settings.myPhotosEnabled && !settings.communityPhotosEnabled,
          community_only: settings.communityPhotosEnabled && !settings.myPhotosEnabled,
        }),
      });
      if (!res.ok) throw new Error("Search failed");
      const data = await res.json();
      const items: NearbyRouteItem[] = data.items ?? [];
      setProximityResults(items);
      setProximityCount(items.length);
      // Auto-add all results to waypoints
      if (items.length > 0) {
        setWaypoints((prev) => {
          let next = [...prev];
          for (const item of items) {
            if (next.some((w) => w.panoId === item.id)) continue;
            const newWp: Waypoint = {
              panoId: item.id,
              name: item.name ?? "Untitled",
              lat: item.lat,
              lng: item.lng,
              thumbnailUrl: item.thumbnail_url,
            };
            const withFrac = next.map((w, i) => ({ w, i, frac: i / Math.max(next.length - 1, 1) }));
            const insertAfter = withFrac.filter((x) => x.frac <= item.route_fraction).pop();
            if (!insertAfter) {
              next = [newWp, ...next];
            } else {
              next.splice(insertAfter.i + 1, 0, newWp);
            }
          }
          return next;
        });
      }
    } catch {
      setProximityResults([]);
      setProximityCount(0);
    } finally {
      setProximityLoading(false);
    }
  }, [displayTrkpts, settings.proximityRadiusM, settings.myPhotosEnabled, settings.communityPhotosEnabled]);

  // ── Convert proximity result to waypoint and add ──────────────────────────
  const addFromProximity = useCallback((item: NearbyRouteItem) => {
    setWaypoints((prev) => {
      if (prev.some((w) => w.panoId === item.id)) return prev;
      const newWp: Waypoint = {
        panoId: item.id,
        name: item.name ?? "Untitled",
        lat: item.lat,
        lng: item.lng,
        thumbnailUrl: item.thumbnail_url,
      };
      // Insert at position corresponding to route_fraction order
      const withFrac = [...prev.map((w, i) => ({ w, i, frac: i / Math.max(prev.length - 1, 1) }))];
      const insertAfter = withFrac.filter((x) => x.frac <= item.route_fraction).pop();
      if (!insertAfter) return [newWp, ...prev];
      const idx = insertAfter.i + 1;
      const next = [...prev];
      next.splice(idx, 0, newWp);
      return next;
    });
  }, []);

  const addAllProximityResults = useCallback(() => {
    proximityResults.forEach(addFromProximity);
  }, [proximityResults, addFromProximity]);

  // ── Waypoint management (from map click) ─────────────────────────────────
  const addWaypoint = useCallback((feature: { properties: { id: string; name?: string | null; thumbnail_url?: string | null }; geometry: { coordinates: number[] } }) => {
    const id = feature.properties.id;
    setWaypoints((prev) => {
      if (prev.some((w) => w.panoId === id)) return prev;
      const [lng, lat] = feature.geometry.coordinates as [number, number];
      return [...prev, {
        panoId: id,
        name: feature.properties.name ?? "Untitled",
        lat,
        lng,
        thumbnailUrl: feature.properties.thumbnail_url ?? null,
      }];
    });
    setIsAddingMode(false);
  }, []);

  const removeWaypoint = useCallback((panoId: string) => {
    setWaypoints((prev) => prev.filter((w) => w.panoId !== panoId));
  }, []);

  const reorderWaypoints = useCallback((newOrder: Waypoint[]) => {
    setWaypoints(newOrder);
  }, []);

  // ── Save / Load / Delete ─────────────────────────────────────────────────
  const saveRoute = useCallback(() => {
    if (!gpxRoute) return;
    const entry: SavedGpxRoute = {
      id: generateId(),
      name: settings.routeName,
      settings,
      rawTrkpts: gpxRoute.trkpts,
      waypoints,
      createdAt: new Date().toISOString(),
    };
    const updated = [entry, ...readSaved()];
    writeSaved(updated);
    setSavedRoutes(updated);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  }, [gpxRoute, settings, waypoints]);

  const loadRoute = useCallback((route: SavedGpxRoute) => {
    setGpxRoute({
      name: route.name,
      trkpts: route.rawTrkpts,
      totalDistanceM: route.rawTrkpts[route.rawTrkpts.length - 1]?.distFromStart ?? 0,
      elevationGainM: 0,
      elevationLossM: 0,
      durationSec: null,
    });
    setSettingsState(route.settings);
    setWaypoints(route.waypoints);
    setProximityResults([]);
  }, []);

  const deleteRoute = useCallback((id: string) => {
    const updated = readSaved().filter((r) => r.id !== id);
    writeSaved(updated);
    setSavedRoutes(updated);
  }, []);

  // ── Export GPX ───────────────────────────────────────────────────────────
  const exportGpx = useCallback(() => {
    const trkpts = displayTrkpts
      .map((p) => `    <trkpt lat="${p.lat}" lon="${p.lng}">${p.ele != null ? `<ele>${p.ele}</ele>` : ""}${p.time ? `<time>${p.time}</time>` : ""}</trkpt>`)
      .join("\n");
    const wpts = waypoints
      .map((w) => `  <wpt lat="${w.lat}" lon="${w.lng}"><name>${xmlEscape(w.name)}</name></wpt>`)
      .join("\n");
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="360Photos">
  <trk><name>${xmlEscape(settings.routeName)}</name><trkseg>
${trkpts}
  </trkseg></trk>
${wpts}
</gpx>`;
    const blob = new Blob([gpx], { type: "application/gpx+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${settings.routeName.replace(/[^a-z0-9]/gi, "_")}.gpx`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [displayTrkpts, waypoints, settings.routeName]);

  // ── Share URL ────────────────────────────────────────────────────────────
  const copyShareLink = useCallback(() => {
    if (!gpxRoute) return;
    // Simplify to ~500 points max for URL encoding
    const simplified = simplifyTrack(gpxRoute.trkpts, gpxRoute.trkpts.length > 500 ? 0.0001 : 0);
    const payload = {
      name: settings.routeName,
      settings,
      rawTrkpts: simplified,
      waypoints,
    };
    const encoded = btoa(JSON.stringify(payload));
    const url = `${window.location.origin}${window.location.pathname}?gpx=${encoded}`;
    navigator.clipboard.writeText(url).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, [gpxRoute, settings, waypoints]);

  // ── Create Tour ──────────────────────────────────────────────────────────
  const createTour = useCallback(async (onSuccess?: (collectionId: string) => void) => {
    if (waypoints.length === 0) return;
    setTourCreating(true);
    try {
      // 1. Create collection
      const colRes = await fetch(`${API}/api/collections`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: settings.routeName,
          description: settings.description || undefined,
        }),
      });
      if (!colRes.ok) throw new Error("Failed to create collection");
      const col = await colRes.json();
      const colId = col.id;

      // 2. Add waypoints in order
      for (let i = 0; i < waypoints.length; i++) {
        await fetch(
          `${API}/api/collections/${colId}/panoramas/${waypoints[i].panoId}?sort_order=${i}`,
          { method: "POST", credentials: "include" },
        );
      }

      // 3. Apply routing settings
      await fetch(`${API}/api/collections/${colId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          route_show_routing: true,
          route_travel_mode: settings.travelMode,
          route_speed: "average",
          route_camera_mode: "cinematic",
          route_vehicle_style: settings.travelMode === "walking" ? "walking" : settings.travelMode === "bicycling" ? "bike" : "car",
        }),
      });

      onSuccess?.(colId);
    } catch {
      // Surface error silently — caller can check tourCreating going false
    } finally {
      setTourCreating(false);
    }
  }, [waypoints, settings]);

  // ── Panel open/close ─────────────────────────────────────────────────────
  const open  = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => { setIsOpen(false); setIsAddingMode(false); }, []);
  const toggle = useCallback(() => setIsOpen((v) => {
    if (v) setIsAddingMode(false);
    return !v;
  }), []);

  return {
    isOpen, open, close, toggle,
    gpxRoute, parseError,
    handleFileUpload,
    settings, updateSetting,
    displayTrkpts,
    trimmedGeoJson,
    waypoints, addWaypoint, removeWaypoint, reorderWaypoints,
    proximityResults, proximityLoading, proximityCount,
    searchProximity, addFromProximity, addAllProximityResults,
    isAddingMode, setIsAddingMode,
    searchQuery, setSearchQuery,
    savedRoutes, saveRoute, loadRoute, deleteRoute,
    savedFlash,
    exportGpx,
    copyShareLink, shareCopied,
    createTour, tourCreating,
  };
}

export type UseGpxRouteReturn = ReturnType<typeof useGpxRoute>;
